export const BATTLEYE_R2_NEW_USE_OSS = process.env.NODE_ENV === "production";

const LOCAL_PREFIX = `${process.env.BASE_URL}detail/battleye-r2-new/`;
const OSS_SUFFIX = "battleye_r2_new/";
const DEFAULT_EXTENSION = "png";

const BATTLEYE_R2_NEW_VARIANT_ASSETS = new Set([
  "battleye_r2_new_kv",
  "battleye_r2_new_selling",
  "battleye_r2_new_01",
  "battleye_r2_new_nameplate_kv"
]);

type VariantSlot = "desktop_cn" | "mobile_cn" | "desktop_en" | "mobile_en";

const BATTLEYE_R2_NEW_VARIANT_FILES: Record<
  string,
  Partial<Record<VariantSlot, string>>
> = {
  battleye_r2_new_kv: {
    desktop_cn: "battleye_r2_new_kv_cn.jpg",
    mobile_cn: "battleye_r2_new_kv_mobile_cn.png",
    desktop_en: "battleye_r2_new_kv_en.jpg",
    mobile_en: "battleye_r2_new_kv_mobile_en.png"
  },
  battleye_r2_new_nameplate_kv: {
    desktop_cn: "battleye_r2_new_nameplate_kv_cn.jpg",
    mobile_cn: "battleye_r2_new_nameplate_kv_mobile_cn.png"
  },
  battleye_r2_new_selling: {
    desktop_cn: "battleye_r2_new_selling_cn.jpg",
    desktop_en: "battleye_r2_new_selling_en.jpg"
  },
  battleye_r2_new_01: {
    desktop_cn: "battleye_r2_new_01_cn.png",
    mobile_cn: "battleye_r2_new_01_mobile_cn.png",
    desktop_en: "battleye_r2_new_01_en.png",
    mobile_en: "battleye_r2_new_01_mobile_en.png"
  }
};

export const BATTLEYE_R2_NEW_ASSET_EXTENSIONS: Record<string, string> = {
  battleye_r2_new_kv: "png",
  battleye_r2_new_selling: "png",
  battleye_r2_new_selling_mobile_cn: "png",
  battleye_r2_new_01: "png",
  battleye_r2_new_nameplate_kv: "png",
  battleye_r2_new_01_cn_pc_01: "jpg",
  battleye_r2_new_01_cn_pc_02: "jpg",
  battleye_r2_new_02: "gif",
  battleye_r2_new_03: "gif",
  battleye_r2_new_04_01: "jpg",
  battleye_r2_new_04_02: "jpg",
  battleye_r2_new_04_03: "png",
  battleye_r2_new_05_01: "jpg",
  battleye_r2_new_05_02: "jpg",
  battleye_r2_new_05_03: "jpg",
  battleye_r2_new_05_04: "jpg",
  battleye_r2_new_05_05: "jpg",
  battleye_r2_new_05_06: "jpg",
  battleye_r2_new_05_07: "jpg",
  battleye_r2_new_05_08: "jpg",
  battleye_r2_new_06: "jpeg",
  battleye_r2_new_07_01: "gif",
  battleye_r2_new_07_02: "gif",
  battleye_r2_new_08: "jpg",
  battleye_r2_new_09: "jpg",
  battleye_r2_new_10: "jpeg",
  battleye_r2_new_10_pc: "png",
  battleye_r2_new_11_01: "png",
  battleye_r2_new_11_02: "png",
  battleye_r2_new_12: "jpg",
  battleye_r2_new_13: "jpg"
};

interface BuildAssetOptions {
  assetKey: string;
  isCN: boolean;
  isMobile: boolean;
  oss: string;
  useOss?: boolean;
}

const buildVariantName = (
  assetKey: string,
  locale: "cn" | "en",
  isMobile: boolean
) =>
  BATTLEYE_R2_NEW_VARIANT_FILES[assetKey]?.[
    `${isMobile ? "mobile" : "desktop"}_${locale}` as VariantSlot
  ] || "";

const buildSingleAssetName = (assetKey: string) => {
  const extension =
    BATTLEYE_R2_NEW_ASSET_EXTENSIONS[assetKey] || DEFAULT_EXTENSION;

  return `${assetKey}.${extension}`;
};

const unique = (values: string[]) =>
  values.filter((value, index) => values.indexOf(value) === index);

export const getBattleyeR2NewAssetExtension = (assetKey: string): string =>
  BATTLEYE_R2_NEW_ASSET_EXTENSIONS[assetKey] || DEFAULT_EXTENSION;

export const isBattleyeR2NewVideo = (assetKey: string): boolean =>
  ["mp4", "webm", "mov"].includes(getBattleyeR2NewAssetExtension(assetKey));

export const buildBattleyeR2NewAssetCandidates = ({
  assetKey,
  isCN,
  isMobile,
  oss,
  useOss = BATTLEYE_R2_NEW_USE_OSS
}: BuildAssetOptions): string[] => {
  // 中文页在本地开发时，部分图片只存在于 OSS，这里把两个来源都放进候选列表。
  const prefixes = unique([
    useOss ? `${oss}${OSS_SUFFIX}` : LOCAL_PREFIX,
    useOss ? LOCAL_PREFIX : `${oss}${OSS_SUFFIX}`
  ]);

  if (!BATTLEYE_R2_NEW_VARIANT_ASSETS.has(assetKey)) {
    return prefixes.map(prefix => `${prefix}${buildSingleAssetName(assetKey)}`);
  }

  const locale = isCN ? "cn" : "en";
  const variants = unique([
    buildVariantName(assetKey, locale, isMobile),
    buildVariantName(assetKey, locale, !isMobile),
    buildVariantName(assetKey, "cn", isMobile),
    buildVariantName(assetKey, "cn", !isMobile),
    buildSingleAssetName(assetKey)
  ]).filter(Boolean);

  const reorderedVariants =
    assetKey === "battleye_r2_new_10"
      ? unique(
          isMobile
            ? [
                buildVariantName(assetKey, "cn", true),
                buildSingleAssetName(assetKey)
              ]
            : [
                buildVariantName(assetKey, "cn", false),
                buildSingleAssetName(assetKey)
              ]
        )
      : assetKey === "battleye_r2_new_nameplate_kv"
      ? unique([
          buildVariantName(assetKey, locale, isMobile),
          buildSingleAssetName(assetKey)
        ])
      : variants;

  return unique(
    prefixes.flatMap(prefix =>
      reorderedVariants.map(variant => `${prefix}${variant}`)
    )
  );
};

export const resolveBattleyeR2NewAsset = (options: BuildAssetOptions): string =>
  buildBattleyeR2NewAssetCandidates(options)[0];
